import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { formatCents } from "@shared/money";
import { catalogItemInput, catalogItemPatch } from "@shared/schemas/catalog";
import { validateCatalog } from "@shared/validation/admin";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery, useBatchValidation } from "@web/lib/validation";
import { api, catalogQuery, venuesQuery, type CatalogItem } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, EmptyState, Inline, MoneyField, Page, Picker, Row, RowGroup, Skeleton, Stack, StatusChip, Text, TextArea, TextField, Toggle, UnsavedChanges, ValidationBadge, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

const KINDS = [
  { value: "package", label: "Package" },
  { value: "fee", label: "Fee" },
  { value: "addon", label: "Add-on" },
  { value: "discount", label: "Discount" },
];
const PRICING = [
  { value: "flat", label: "Flat" },
  { value: "hourly", label: "Hourly" },
  { value: "per_guest", label: "Per guest" },
];
const unit = (p: string) => (p === "hourly" ? "/hr" : p === "per_guest" ? "/guest" : "");

const EMPTY = { venueId: "", name: "", description: "", kind: "package", pricing: "flat", pricingAction: "", defaultAmount: 0 as number | null, friSun: null as number | null, sat: null as number | null, weekday: null as number | null, minHours: "", includedHours: "", autoAddWithPackage: false, active: true, sort: "0" };
type Form = typeof EMPTY;
const fromItem = (c: CatalogItem): Form => ({
  venueId: c.venueId ?? "",
  name: c.name,
  description: c.description ?? "",
  kind: c.kind,
  pricing: c.pricing,
  pricingAction: c.pricingAction ?? "",
  defaultAmount: c.defaultAmount,
  friSun: c.dayRates?.fri_sun ?? null,
  sat: c.dayRates?.sat ?? null,
  weekday: c.dayRates?.weekday ?? null,
  minHours: c.minHours?.toString() ?? "",
  includedHours: c.includedHours?.toString() ?? "",
  autoAddWithPackage: Boolean(c.autoAddWithPackage),
  active: Boolean(c.active),
  sort: String(c.sort),
});

/** Settings › Catalog (SPEC §3.3, §10.2): items grouped by venue; edit sheet with day rates. */
export function SettingsCatalog() {
  const me = useMe();
  const items = useQuery(catalogQuery({ includeInactive: true }));
  const venues = useQuery(venuesQuery(true));
  const checks = useBatchValidation(me.companyAdmin ? (items.data ?? []).map(row => ({ entityType: "catalog", id: row.id })) : []);
  const [editing, setEditing] = useState<CatalogItem | "new" | null>(null);
  const groups = useMemo(() => {
    const byVenue = new Map<string | null, CatalogItem[]>();
    for (const c of items.data ?? []) byVenue.set(c.venueId, [...(byVenue.get(c.venueId) ?? []), c]);
    const order: (string | null)[] = [null, ...(venues.data ?? []).map((v) => v.id)];
    return order.filter((k) => byVenue.has(k)).map((k) => ({ key: k ?? "all", title: k ? (venues.data?.find((v) => v.id === k)?.name ?? "Venue") : "All venues", items: byVenue.get(k)! }));
  }, [items.data, venues.data]);
  if (!me.companyAdmin) return <AdminsOnly />;
  return (
    <Page>
      <Stack gap={4}>
        {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
        {items.isError ? (
          <EmptyState title="The catalog could not be loaded" body={errorMessage(items.error)} action={<Button onClick={() => void items.refetch()}>Try again</Button>} />
        ) : !items.data ? (
          <Skeleton count={5} />
        ) : groups.length === 0 ? (
          <EmptyState title="No catalog items" body="Packages, fees, add-ons and discounts the booker picks from." />
        ) : (
          groups.map((g) => (
            <Stack key={g.key} gap={2}>
              <Text variant="headline">{g.title}</Text>
              <RowGroup>
                {g.items.map((c) => (
                  <Row
                    key={c.id}
                    title={c.name}
                    subtitle={`${KINDS.find((k) => k.value === c.kind)?.label ?? c.kind} · ${c.pricing}${c.dayRates ? " · day rates" : ""}${c.autoAddWithPackage ? " · auto-add" : ""}`}
                    trailing={
                      <Stack gap={1}>
                        {!c.active && <StatusChip>Inactive</StatusChip>}
                        <Text as="span" variant="body" tabular>
                          {c.pricingAction === "pay_in_full_discount" ? "5%" : c.pricingAction === "late_fee" ? "Overdue %" : formatCents(c.defaultAmount)}
                          {unit(c.pricing)}
                        </Text>
                        {checks.isFetching && !checks.data ? <StatusChip>Checking…</StatusChip> : <ValidationBadge result={checks.data?.records.find(r => r.entity.type === "catalog" && r.entity.id === c.id)} unavailable={checks.isError} />}
                      </Stack>
                    }
                    onClick={() => setEditing(c)}
                  />
                ))}
              </RowGroup>
            </Stack>
          ))
        )}
        <Button variant="secondary" icon={<Plus aria-hidden size={18} />} onClick={() => setEditing("new")}>
          Add item
        </Button>
      </Stack>
      {editing && <ItemSheet key={editing === "new" ? "new" : editing.id} item={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}

function ItemSheet({ item, onClose }: { item: CatalogItem | null; onClose: () => void }) {
  const toast = useToast();
  const venues = useQuery(venuesQuery(true));
  const saved = useQuery(recordValidationQuery("catalog", item?.id ?? ""));
  const [f, setF] = useState<Form>(item ? fromItem(item) : EMPTY);
  const toBody = (f: Form) => {
    const hasRates = f.friSun !== null || f.sat !== null || f.weekday !== null;
    const dayRates = hasRates ? { fri_sun: f.friSun ?? undefined, sat: f.sat ?? undefined, weekday: f.weekday ?? undefined } : null;
    return {
      venueId: f.venueId || null,
      name: f.name,
      description: f.description.trim() || null,
      kind: f.kind,
      pricing: f.pricing,
      pricingAction: f.pricingAction || null,
      defaultAmount: f.defaultAmount,
      dayRates,
      minHours: f.minHours ? Number(f.minHours) : null,
      includedHours: f.includedHours ? Number(f.includedHours) : null,
      autoAddWithPackage: f.autoAddWithPackage,
      active: f.active,
      sort: Number(f.sort),
    };
  };
  const body = item ? changedValues(toBody(f), toBody(fromItem(item))) : toBody(f);
  const parsed = parseInput(item ? catalogItemPatch : catalogItemInput, body, { type: "catalog", id: item?.id }, "catalog.save");
  const validation = useFormValidation(evaluation([...parsed.issues, ...validateCatalog({ ...item, ...body }, { before: item ?? undefined, changedFields: Object.keys(body) }).issues], false));
  const { busy, field } = validation;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => validation.edit(() => setF((x) => ({ ...x, [k]: v })));
  async function save() {
    if (!parsed.success || !validation.begin("catalog.save")) return;
    try {
      if (item) await api.patchCatalogItem(item.id, parsed.data);
      else await api.createCatalogItem(catalogItemInput.parse(body));
      toast.success(item ? "Item saved" : "Item added");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title={item ? "Edit item" : "New item"}
      footer={
        <Button loading={busy} onClick={save}>
          {item ? "Save item" : "Add item"}
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && JSON.stringify(f) !== JSON.stringify(item ? fromItem(item) : EMPTY)} />
        {item && <ValidationSummary result={saved.data} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved />}
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <TextField label="Name" value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus={!item} {...field("name")} />
        <TextArea label="Description" value={f.description} onChange={(e) => set("description", e.target.value)} {...field("description")} />
        <Picker label="Venue" placeholder="All venues" value={f.venueId} onChange={(v) => set("venueId", v)} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} {...field("venueId")} />
        <Inline gap={2} align="start">
          <Picker label="Kind" className="flex-1" value={f.kind} onChange={(v) => set("kind", v)} options={KINDS} {...field("kind")} />
          <Picker label="Pricing" className="flex-1" value={f.pricing} onChange={(v) => set("pricing", v)} options={PRICING} {...field("pricing")} />
        </Inline>
        <Picker label="Pricing preset" value={f.pricingAction} onChange={(v) => set("pricingAction", v)} options={[{ value: "", label: "Regular amount" }, { value: "pay_in_full_discount", label: "Pay-in-full discount · 5%" }, { value: "late_fee", label: "Late fee · company overdue percentage" }]} {...field("pricingAction")} />
        {f.pricingAction && <Text variant="caption" tone="secondary">Calculated when staff adds the line. Never automatic; the pay-in-full discount requires confirmation.</Text>}
        <MoneyField label="Default amount" value={f.defaultAmount} onChange={(v) => set("defaultAmount", v)} hint={f.kind === "discount" ? "Applied as a negative line" : "Used when no day rate matches"} {...field("defaultAmount")} />
        <Stack gap={2}>
          <Text variant="headline">Day rates</Text>
          <Text variant="caption" tone="tertiary">
            Resolved from the event date; leave a rate empty to fall back to the default amount.
          </Text>
          <Inline gap={2} align="start">
            <MoneyField label="Fri / Sun" className="flex-1" value={f.friSun} onChange={(v) => set("friSun", v)} {...field("dayRates.fri_sun")} />
            <MoneyField label="Saturday" className="flex-1" value={f.sat} onChange={(v) => set("sat", v)} {...field("dayRates.sat")} />
            <MoneyField label="Weekday" className="flex-1" value={f.weekday} onChange={(v) => set("weekday", v)} {...field("dayRates.weekday")} />
          </Inline>
        </Stack>
        <Inline gap={2} align="start">
          <TextField label="Min hours" type="number" inputMode="numeric" min={0} className="flex-1" value={f.minHours} onChange={(e) => set("minHours", e.target.value)} {...field("minHours")} />
          <TextField label="Included hours" type="number" inputMode="numeric" min={0} className="flex-1" value={f.includedHours} onChange={(e) => set("includedHours", e.target.value)} {...field("includedHours")} />
          <TextField label="Sort" type="number" inputMode="numeric" className="flex-1" value={f.sort} onChange={(e) => set("sort", e.target.value)} {...field("sort")} />
        </Inline>
        <Toggle label="Auto-add with a package" description="Added to every new booking that picks a package (e.g. cleaning)" checked={f.autoAddWithPackage} onChange={(v) => set("autoAddWithPackage", v)} disabled={busy} />
        <Toggle label="Active" checked={f.active} onChange={(v) => set("active", v)} disabled={busy} />
      </Stack>
    </BottomSheet>
  );
}
